/* ==========================================================================
   BlockPrintsIndia — shared UI behaviour
   Depends on global.js only for trapFocus / removeTrapFocus (optional).
   ========================================================================== */

(function () {
  'use strict';

  const trap = (container, el) =>
    typeof window.trapFocus === 'function' ? window.trapFocus(container, el) : el && el.focus();
  const untrap = (el) => (typeof window.removeTrapFocus === 'function' ? window.removeTrapFocus(el) : el && el.focus());

  /* ---------- Scrim: shared by every overlay, reference counted ---------- */

  const Scrim = {
    el: null,
    users: new Set(),
    node() {
      if (!this.el) this.el = document.querySelector('[data-bpi-scrim]');
      return this.el;
    },
    show(user) {
      const node = this.node();
      if (!node) return;
      this.users.add(user);
      node.hidden = false;
      // Force layout so the browser registers opacity:0 before the transition
      // starts. A rAF would be throttled to never in a backgrounded tab, which
      // would leave the scrim permanently invisible.
      void node.offsetHeight;
      node.setAttribute('data-open', '');
      document.body.classList.add('bpi-no-scroll');
    },
    hide(user) {
      const node = this.node();
      if (!node) return;
      this.users.delete(user);
      if (this.users.size) return;
      node.removeAttribute('data-open');
      document.body.classList.remove('bpi-no-scroll');
      const done = () => {
        if (!this.users.size) node.hidden = true;
      };
      node.addEventListener('transitionend', done, { once: true });
      setTimeout(done, 400);
    },
    onClick(handler) {
      const node = this.node();
      if (node) node.addEventListener('click', handler);
    },
  };

  window.BpiScrim = Scrim;

  /* ---------- Drawer ---------- */

  class BpiDrawer extends HTMLElement {
    connectedCallback() {
      this.opener = null;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-bpi-close]')) this.close();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
      Scrim.onClick(() => {
        if (this.hasAttribute('data-open')) this.close();
      });
    }

    open(opener) {
      if (this.hasAttribute('data-open')) return;
      this.opener = opener || null;
      this.setAttribute('data-open', '');
      this.setAttribute('aria-hidden', 'false');
      Scrim.show(this);
      const focusTarget = this.querySelector('[data-bpi-autofocus]') || this.querySelector('[data-bpi-close]');
      // wait for the slide-in so focus doesn't fight the transform
      setTimeout(() => trap(this, focusTarget), 60);
      this.dispatchEvent(new CustomEvent('bpi:drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.hasAttribute('data-open')) return;
      this.removeAttribute('data-open');
      this.setAttribute('aria-hidden', 'true');
      Scrim.hide(this);
      untrap(this.opener);
      this.dispatchEvent(new CustomEvent('bpi:drawer:close', { bubbles: true }));
    }

    toggle(opener) {
      this.hasAttribute('data-open') ? this.close() : this.open(opener);
    }
  }

  customElements.define('bpi-drawer', BpiDrawer);

  // Global openers: <button data-bpi-drawer-open="MenuDrawer">
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-bpi-drawer-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.getAttribute('data-bpi-drawer-open'));
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(trigger);
  });

  /* ---------- Modal (fabric spec, size chart, quick view) ---------- */

  class BpiModal extends HTMLElement {
    connectedCallback() {
      this.opener = null;
      this.addEventListener('click', (e) => {
        if (e.target === this || e.target.closest('[data-bpi-close]')) this.close();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    open(opener) {
      this.opener = opener || null;
      this.setAttribute('data-open', '');
      document.body.classList.add('bpi-no-scroll');
      setTimeout(() => trap(this, this.querySelector('[data-bpi-close]')), 60);
    }

    close() {
      this.removeAttribute('data-open');
      document.body.classList.remove('bpi-no-scroll');
      untrap(this.opener);
    }
  }

  customElements.define('bpi-modal', BpiModal);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-bpi-modal-open]');
    if (!trigger) return;
    const modal = document.getElementById(trigger.getAttribute('data-bpi-modal-open'));
    if (!modal || typeof modal.open !== 'function') return;
    event.preventDefault();
    modal.open(trigger);
  });

  /* ---------- Horizontal rail: arrows + progress ---------- */

  class BpiRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rail-track]');
      if (!this.track) return;

      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.bar = this.querySelector('[data-rail-bar]');

      this.prev && this.prev.addEventListener('click', () => this.scrollBy(-1));
      this.next && this.next.addEventListener('click', () => this.scrollBy(1));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });

      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      this.resizeObserver && this.resizeObserver.disconnect();
    }

    step() {
      const first = this.track.firstElementChild;
      if (!first) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
      return first.getBoundingClientRect().width + gap;
    }

    scrollBy(direction) {
      this.track.scrollBy({ left: this.step() * direction, behavior: 'smooth' });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const atStart = this.track.scrollLeft <= 1;
      const atEnd = this.track.scrollLeft >= max - 1;

      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;
      this.toggleAttribute('data-no-overflow', max <= 1);

      if (this.bar) {
        const ratio = this.track.clientWidth / this.track.scrollWidth;
        const progress = max > 0 ? this.track.scrollLeft / max : 0;
        this.bar.style.width = `${Math.min(ratio, 1) * 100}%`;
        this.bar.style.transform = `translateX(${(progress * (1 / Math.min(ratio, 1) - 1)) * 100}%)`;
      }
    }
  }

  customElements.define('bpi-rail', BpiRail);

  /* ---------- Tabs ---------- */

  class BpiTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab));
        tab.addEventListener('keydown', (e) => this.onKeydown(e));
      });
    }

    onKeydown(event) {
      const i = this.tabs.indexOf(event.target);
      if (i === -1) return;
      let next = null;
      if (event.key === 'ArrowRight') next = this.tabs[(i + 1) % this.tabs.length];
      if (event.key === 'ArrowLeft') next = this.tabs[(i - 1 + this.tabs.length) % this.tabs.length];
      if (!next) return;
      event.preventDefault();
      this.select(next);
      next.focus();
    }

    select(tab) {
      this.tabs.forEach((t) => {
        const selected = t === tab;
        t.setAttribute('aria-selected', String(selected));
        t.setAttribute('tabindex', selected ? '0' : '-1');
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
      // rails inside a freshly shown panel measured 0 while hidden
      this.querySelectorAll('bpi-rail').forEach((rail) => rail.update && rail.update());
    }
  }

  customElements.define('bpi-tabs', BpiTabs);

  /* ---------- Sticky add-to-cart bar ---------- */

  class BpiStickyBar extends HTMLElement {
    connectedCallback() {
      const watchSelector = this.getAttribute('data-watch');
      const watched = watchSelector && document.querySelector(watchSelector);
      if (!watched) return;

      this.observer = new IntersectionObserver(
        ([entry]) => {
          // show once the buy box has scrolled out of view upward
          const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.toggleAttribute('data-visible', past);
        },
        { threshold: 0, rootMargin: '0px' }
      );
      this.observer.observe(watched);
    }

    disconnectedCallback() {
      this.observer && this.observer.disconnect();
    }
  }

  customElements.define('bpi-sticky-bar', BpiStickyBar);

  /* ---------- Product gallery: thumbnail rail + main image ---------- */

  class BpiGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.show(thumb.dataset.galleryThumb, true));
      });

      if (this.main) {
        this.main.addEventListener('scroll', () => this.syncFromScroll(), { passive: true });
      }
    }

    show(id, scroll) {
      this.thumbs.forEach((t) => t.setAttribute('aria-current', String(t.dataset.galleryThumb === id)));
      if (!scroll || !this.main) return;
      const slide = this.slides.find((s) => s.dataset.gallerySlide === id);
      if (slide) this.main.scrollTo({ left: slide.offsetLeft - this.main.offsetLeft, behavior: 'smooth' });
    }

    syncFromScroll() {
      if (!this.main) return;
      const mid = this.main.scrollLeft + this.main.clientWidth / 2;
      const current = this.slides.find((s) => s.offsetLeft <= mid && s.offsetLeft + s.offsetWidth > mid);
      if (current) this.show(current.dataset.gallerySlide, false);
    }
  }

  customElements.define('bpi-gallery', BpiGallery);

  /* ---------- Quantity stepper ---------- */

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-qty-step]');
    if (!btn) return;
    const wrap = btn.closest('[data-qty]');
    const input = wrap && wrap.querySelector('input');
    if (!input) return;

    const step = parseInt(btn.dataset.qtyStep, 10);
    const min = parseInt(input.min || '1', 10);
    const max = input.max ? parseInt(input.max, 10) : Infinity;
    const next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || min) + step));

    if (next === parseInt(input.value, 10)) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Announcement bar rotation ---------- */

  class BpiAnnouncement extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
      if (this.slides.length < 2) return;

      this.index = 0;
      this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.go(-1));
      this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.go(1));

      const interval = parseInt(this.getAttribute('data-autoplay') || '0', 10);
      if (interval > 0) {
        this.timer = setInterval(() => this.go(1), interval * 1000);
        this.addEventListener('mouseenter', () => clearInterval(this.timer));
      }
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    go(direction) {
      this.slides[this.index].hidden = true;
      this.index = (this.index + direction + this.slides.length) % this.slides.length;
      this.slides[this.index].hidden = false;
    }
  }

  customElements.define('bpi-announcement', BpiAnnouncement);

  /* ---------- Desktop navigation bar ----------
     Panels are driven entirely from JS rather than a CSS :hover rule, so pointer,
     touch and keyboard all go through one code path and a click cannot fight a
     hover state. */

  const navItems = Array.from(document.querySelectorAll('[data-bpi-nav-item]'));

  if (navItems.length) {
    let closeTimer = null;

    const setNavOpen = (item, open) => {
      const toggle = item.querySelector('[data-bpi-nav-toggle]');
      const panel = item.querySelector('[data-bpi-nav-panel]');
      if (!toggle || !panel) return;
      item.toggleAttribute('data-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
    };

    const closeAllNav = (except) => {
      navItems.forEach((item) => {
        if (item !== except) setNavOpen(item, false);
      });
    };

    const canHover = window.matchMedia('(hover: hover) and (min-width: 990px)').matches;

    navItems.forEach((item) => {
      const toggle = item.querySelector('[data-bpi-nav-toggle]');
      if (!toggle) return;

      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        const isOpen = item.hasAttribute('data-open');
        closeAllNav(item);
        setNavOpen(item, !isOpen);
      });

      if (canHover) {
        item.addEventListener('mouseenter', () => {
          clearTimeout(closeTimer);
          closeAllNav(item);
          setNavOpen(item, true);
        });

        // Grace period so a diagonal cursor path into the panel survives
        item.addEventListener('mouseleave', () => {
          clearTimeout(closeTimer);
          closeTimer = setTimeout(() => setNavOpen(item, false), 180);
        });
      }

      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) setNavOpen(item, false);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const open = navItems.find((item) => item.hasAttribute('data-open'));
      if (!open) return;
      setNavOpen(open, false);
      open.querySelector('[data-bpi-nav-toggle]')?.focus();
    });

    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-bpi-nav-item]')) closeAllNav();
    });
  }

  /* ---------- Cart drawer cross-sell ----------
     Dawn's <product-recommendations> loads itself from an IntersectionObserver.
     Inside the cart drawer the element starts translated off-screen, so that
     observer never fires on the first open. Trigger the load explicitly when the
     drawer becomes active, and only once per product. */

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    const loadCrossSell = () => {
      if (!cartDrawer.classList.contains('active')) return;
      cartDrawer.querySelectorAll('product-recommendations').forEach((el) => {
        if (el.dataset.bpiLoaded === 'true' || !el.dataset.productId) return;
        el.dataset.bpiLoaded = 'true';
        el.loadRecommendations?.(el.dataset.productId);
      });
    };

    new MutationObserver(loadCrossSell).observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['class'],
    });
    loadCrossSell();
  }

  /* ---------- Search overlay: focus the input when it opens ---------- */

  document.addEventListener('bpi:drawer:open', (event) => {
    const input = event.target.querySelector('input[type="search"]');
    if (input) setTimeout(() => input.focus(), 120);
  });

  /* ---------- Header: shadow on scroll ---------- */

  const header = document.querySelector('[data-bpi-header]');
  if (header) {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        header.toggleAttribute('data-scrolled', window.scrollY > 8);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }
})();
